import { useMemo } from "react";
import { currencyFormatter } from "@common/helpers/currency";
import { RefValue } from "@common/types/auditLog";
import { numberFormatter } from "@common/helpers/number";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useEntitiesById } from "@hooks/useEntities";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import styles from "./FinancialAccountValue.module.scss";

interface FinancialAccountValueProps {
    value: RefValue;
    field?: string;
}

const CURRENCY_FIELDS = ["balance", "availableBalance", "creditLimit"] as const;

export const FinancialAccountValue = ({
    value,
    field,
}: FinancialAccountValueProps) => {
    const allEntities = useEntitiesById();
    const rawValue = value?.value;
    const entity = useMemo(
        () =>
            field === "entityId" && rawValue !== null
                ? allEntities.get(Number(rawValue))
                : undefined,
        [field, rawValue, allEntities],
    );

    const isCurrencyField =
        field !== undefined &&
        CURRENCY_FIELDS.includes(field as (typeof CURRENCY_FIELDS)[number]);

    if (!value) {
        return <EmptyValue />;
    }

    if (entity) {
        return (
            <div className={styles.entityContainer}>
                <EntityAvatar entity={entity} size="sm" aria-hidden />
                <span>{entity.name}</span>
            </div>
        );
    }

    if (value.valueLabel) {
        return <span>{value.valueLabel}</span>;
    }

    if (value.value === null || value.value === undefined) {
        return <EmptyValue />;
    }

    if (typeof value.value === "boolean") {
        return (
            <span className={value.value ? styles.success : styles.muted}>
                {value.value ? "Yes" : "No"}
            </span>
        );
    }

    if (typeof value.value === "number" && isCurrencyField) {
        return <span>{currencyFormatter.format(value.value)}</span>;
    }

    if (typeof value.value === "number") {
        return <span>{numberFormatter.format(value.value)}</span>;
    }

    if (typeof value.value === "string") {
        return <span>{value.value}</span>;
    }

    return <EmptyValue />;
};
